import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../api/baseQuery';

// Using centralized baseQueryWithReauth from api/baseQuery.js
export const AnnouncementType = {
  News: 0,
  Campaign: 1
};

export const announcementApi = createApi({
  reducerPath: 'announcementApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Announcement', 'AnnouncementList', 'AnnouncementMedia'],
  keepUnusedDataFor: 300, // 5 dakika cache
  refetchOnMountOrArgChange: true, // Komponent mount olduğunda refetch
  endpoints: (builder) => ({
    getAnnouncements: builder.query({
      query: (params = {}) => {
        // Clean and prepare query parameters
        const queryParams = {
          page: typeof params === 'number' ? params : (params.page || 0),
          pageSize: params.pageSize || 8,
          ...(params.keyword && params.keyword.trim() && { keyword: params.keyword.trim() }),
          ...(params.type !== undefined && { type: params.type }),
          ...(params.status !== undefined && { status: params.status }),
          ...(params.startDate && { startDate: params.startDate }),
          ...(params.endDate && { endDate: params.endDate }),
          // Only include sort parameters if they differ from server defaults
          ...(params.sortBy && { sortBy: params.sortBy }),
          ...(params.sortOrder && { sortOrder: params.sortOrder })
        };

        console.log('🔍 Announcement API Query Params (keyword):', queryParams);

        return {
          url: '/announcement/list',
          method: 'GET',
          params: queryParams
        };
      },
      transformResponse: (response) => {
        if (!response) {
          return {
            value: {
              announcements: [],
              totalCount: 0
            },
            isSuccess: false
          };
        }

        return {
          value: {
            announcements: response.value?.announcements || [],
            totalCount: response.value?.totalCount || 0
          },
          isSuccess: response.isSuccess,
          errors: response.errors || [],
          validationErrors: response.validationErrors || []
        };
      },
      providesTags: (result) => {
        const baseTags = [{ type: 'AnnouncementList', id: 'LIST' }];
        const announcementTags = result?.value?.announcements?.map(({ id }) => ({
          type: 'Announcement',
          id
        })) || [];
        return [...baseTags, ...announcementTags];
      }
    }),

    getAnnouncementsByTenant: builder.query({
      query: (params = {}) => {
        // Clean and prepare query parameters
        const queryParams = {
          page: typeof params === 'number' ? params : (params.page || 0),
          pageSize: params.pageSize || 8,
          ...(params.keyword && params.keyword.trim() && { keyword: params.keyword.trim() }),
          ...(params.type !== undefined && { type: params.type }),
          ...(params.status !== undefined && { status: params.status }),
          ...(params.startDate && { startDate: params.startDate }),
          ...(params.endDate && { endDate: params.endDate }),
          // Only include sort parameters if they differ from server defaults
          ...(params.sortBy && { sortBy: params.sortBy }),
          ...(params.sortOrder && { sortOrder: params.sortOrder })
        };

        console.log('🔍 Announcement Tenant API Query Params (keyword):', queryParams);

        return {
          url: '/announcement/list',
          method: 'GET',
          params: queryParams
        };
      },
      transformResponse: (response) => {
        if (!response) {
          return {
            value: {
              announcements: [],
              totalCount: 0
            },
            isSuccess: false
          };
        }

        return {
          value: {
            announcements: response.value?.announcements || [],
            totalCount: response.value?.totalCount || 0
          },
          isSuccess: response.isSuccess,
          errors: response.errors || [],
          validationErrors: response.validationErrors || []
        };
      },
      providesTags: (result) => {
        const baseTags = [{ type: 'AnnouncementList', id: 'TENANT_LIST' }];
        const announcementTags = result?.value?.announcements?.map(({ id }) => ({
          type: 'Announcement',
          id
        })) || [];
        return [...baseTags, ...announcementTags];
      }
    }),

    getAnnouncementById: builder.query({
      query: (id) => ({
        url: `/announcement/${id}`,
        method: 'GET'
      }),
      transformResponse: (response, meta) => {
        if (meta?.response?.status === 204) {
          return null;
        }
        return response.value;
      },
      providesTags: (result, error, id) => [
        { type: 'Announcement', id },
        { type: 'AnnouncementMedia', id }
      ]
    }),

    createAnnouncement: builder.mutation({
      query: (data) => {
        // Validate required fields
        if (!data.title || !data.description || !data.body) {
          throw new Error('Title, description and body are required');
        }

        return {
          url: '/announcement/create',
          method: 'POST',
          body: {
            ...data,
            description: data.description, // Kısa açıklama
            body: data.body, // Ana içerik (rich text)
          }
        };
      },
      transformErrorResponse: (response) => ({
        status: response.status,
        message: response.data?.message || 'An error occurred while creating the announcement'
      }),
      invalidatesTags: [{ type: 'AnnouncementList', id: 'LIST' }]
    }),

    createTenantAnnouncement: builder.mutation({
      query: (data) => {
        // Validate required fields
        if (!data.title || !data.description || !data.body) {
          throw new Error('Title, description and body are required');
        }

        return {
          url: '/announcement/create',
          method: 'POST',
          body: {
            ...data,
            description: data.description, // Kısa açıklama
            body: data.body, // Ana içerik (rich text)
          }
        };
      },
      transformErrorResponse: (response) => ({
        status: response.status,
        message: response.data?.message || 'An error occurred while creating the announcement'
      }),
      invalidatesTags: [
        { type: 'AnnouncementList', id: 'LIST' },
        { type: 'AnnouncementList', id: 'TENANT_LIST' }
      ]
    }),

    updateAnnouncement: builder.mutation({
      query: (data) => {
        // Validate required fields
        if (!data.title || !data.description || !data.body) {
          throw new Error('Title, description and body are required');
        }

        return {
          url: '/announcement/update',
          method: 'PUT',
          body: {
            ...data,
            description: data.description, // Kısa açıklama
            body: data.body, // Ana içerik (rich text)
          }
        };
      },
      transformResponse: (response, meta) => {
        if (meta?.response?.status === 204) {
          return { isSuccess: true };
        }
        return response;
      },
      invalidatesTags: (result, error, { announcementId }) => [
        { type: 'Announcement', id: announcementId },
        { type: 'AnnouncementList', id: 'LIST' },
        { type: 'AnnouncementMedia', id: announcementId }
      ]
    }),

     deleteAnnouncement: builder.mutation({
      query: (id) => ({
        url: `/announcement/delete/${id}`,
        method: 'DELETE'
      }),
      transformResponse: (response, meta) => {
        if (meta?.response?.status === 204) {
          return { isSuccess: true };
        }
        return response;
      },
      invalidatesTags: (result, error, id) => [
        { type: 'Announcement', id },
        { type: 'AnnouncementList', id: 'LIST' }
      ]
    }), 

    addAnnouncementMedia: builder.mutation({
      query: ({ file, orderNo }) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('orderNo', orderNo.toString());

        return {
          url: '/announcement/media',
          method: 'POST',
          body: formData,
          formData: true
        };
      },
      transformResponse: (response, meta) => {
        if (meta?.response?.status === 204) {
          return { isSuccess: true };
        }
        return response;
      },
      invalidatesTags: (result, error, { announcementId }) => [
        { type: 'AnnouncementMedia', id: announcementId },
        { type: 'Announcement', id: announcementId }
      ]
    })
  })
});

export const {
  useGetAnnouncementsQuery,
  useGetAnnouncementsByTenantQuery,
  useGetAnnouncementByIdQuery,
  useCreateAnnouncementMutation,
  useCreateTenantAnnouncementMutation,
  useUpdateAnnouncementMutation,
  useDeleteAnnouncementMutation,
  useAddAnnouncementMediaMutation
} = announcementApi;

export const {
  reducer: announcementReducer,
  middleware: announcementMiddleware,
  reducerPath: announcementReducerPath
} = announcementApi;